import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output, ViewChild
} from '@angular/core';
import {
  FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

import { Observable, of, throwError } from 'rxjs';
import {
  finalize,
  map,
  switchMap,
  takeUntil,
  tap
} from 'rxjs/operators';

import { COMMON } from '@sondermind/constants';
import { USStateOptions } from '@sondermind/utilities/states-list';
import { CurrentUser } from '@sondermind/data-access/client-user';
import { BreakpointsService } from '@sondermind/utilities/services';
import { ClientInsurance } from '@sondermind/utilities/models-client-insurance';

import {
  ClientInsuranceHttpService,
  IPatientInsurance
} from '@sondermind/data-access/client-insurance';
import {
  IrisButtonContainerAlignments,
  IrisButtonTypes
} from '@sondermind/iris/button';
import { IrisButtonTypeAttributeOptions } from '@sondermind/iris/shared';
import { IconSizes } from '@sondermind/iris/icon';
import { ProviderNetworkCompany } from '@sondermind/utilities/models-client-facing';
import { DropdownOption } from '@sondermind/iris/dropdown';
import { BannerMessage } from '@sondermind/core/design/success-error-banner/success-error-banner.component';

import { PayerDataService } from '../../services/payer.service';

const CIGNA = 'Cigna';
const OPTUM = 'Optum';

@Component({
  selector: 'client-eap-add',
  templateUrl: './eap-add.component.html',
  styleUrls: ['./eap-add.component.scss'],
  standalone: false
})
export class EapAddComponent implements OnInit, OnDestroy {
  @ViewChild('bvErrorBannerContainer') bvErrorBanner: ElementRef<HTMLElement>;
  bvError: BannerMessage;

  // used for embedding form in new client account setup flow
  @Input() useInline: boolean = false;
  @Input() useInSessionRequest: boolean = false;
  @Output() closeAdd: EventEmitter<{ insuranceUpdated: boolean; bvError: boolean; }> = new EventEmitter<{ insuranceUpdated: boolean; bvError: boolean; }>();

  isMobile$ = this.breakpointService.isMobile$;

  eapInfoForm: FormGroup<{
    eapState: FormControl<string>;
    eapProvider: FormControl<ProviderNetworkCompany>;
    authorizationNumber: FormControl<string>;
    startDate: FormControl<Date>;
    endDate: FormControl<Date>;
    eapSessionCount: FormControl<number>;
  }>;

  supportPhoneLink = COMMON.SUPPORT_PHONE_GENERAL_LINK;
  supportPhoneDisplay = COMMON.SUPPORT_PHONE_GENERAL_DISPLAY;

  chooseEapState = '__otherEAP';

  submitting: boolean = false;
  activeStates: string[] = [];

  authNumFormatExample: string = '';

  bvFailedId: number = null;

  private destroyed$ = new EventEmitter<void>();
  eapList$: Observable<ProviderNetworkCompany[]> = of([]);

  get usStateOptions(): Array<{ label: string; value: string; }> {
    return USStateOptions.filter((s) => this.activeStates.includes(s.value));
  }

  authNumberErrorMessages = {
    cignaAuthNumLengthError: 'Must be 9, 10, or 12 characters long.',
    cignaAuthNumNumericalError: 'Must have only numbers after OP or only numbers.',
    optumAuthNumLengthError: 'Must be 8 characters, not including the dash.',
    optumAuthNumEndCharError: 'Last two characters after the dash must be numbers.',
    optumAuthNumMissingDashError: 'Missing dash before last two numbers.',
    optumAuthNumDashError: 'Dash must be before last two numbers.'
  };

  eapSessionCountErrorMessages = {
    min: 'Minimum number of sessions is 1.',
    max: 'Maximum number of sessions is 100.'
  };
  readonly iconSizes = IconSizes;
  IrisButtonContainerAlignments = IrisButtonContainerAlignments;
  IrisButtonTypes = IrisButtonTypes;
  IrisButtonTypeAttributeOptions = IrisButtonTypeAttributeOptions;
  usStateOption: Array<DropdownOption<string>> = [];
  eapListOptions$: Observable<Array<DropdownOption<ProviderNetworkCompany>>> = of([]);

  constructor(
    private breakpointService: BreakpointsService,
    private currentUser: CurrentUser,
    private payerDataService: PayerDataService,
    private insuranceHttp: ClientInsuranceHttpService,
    private snackbar: MatSnackBar,
    private formBuilder: FormBuilder
  ) {}

  ngOnInit(): void {
    this.eapInfoForm = this.formBuilder.group({
      eapState: this.formBuilder.control(this.currentUser.user.insuranceState, Validators.required),
      eapProvider: this.formBuilder.control(null, Validators.required),
      authorizationNumber: this.formBuilder.control(
        '',
        {
          validators: [
            Validators.required,
            this.authNumCignaLengthValidator(),
            this.authNumCignaNumericalValidator(),
            this.authNumOptumLengthValidator(),
            this.authNumOptumEndCharValidator(),
            this.authNumOptumIncludesDashValidator(),
            this.authNumOptumTwoCharAfterDashValidator()
          ],
          updateOn: 'blur'
        }
      ),
      startDate: this.formBuilder.control(new Date(), Validators.required),
      endDate: this.formBuilder.control(null, Validators.required),
      eapSessionCount: this.formBuilder.control(0, [Validators.required, Validators.min(1), Validators.max(100)])
    });

    this.eapInfoForm.get('eapProvider').valueChanges.subscribe(() => {
      this.eapInfoForm.get('authorizationNumber').reset();
    });

    this.eapInfoForm.get('authorizationNumber').valueChanges.subscribe((authNum) => {
      // Add dash to Optum number if user did not
      if (this.eapInfoForm.value.eapProvider?.name.includes(OPTUM) && !authNum?.includes('-') && authNum?.trim().length === 8) {
        this.eapInfoForm.get('authorizationNumber')
          .setValue(`${authNum.slice(0, 6).toLocaleUpperCase()}-${authNum.slice(6)}`);
      }
    });

    this.eapInfoForm.get('eapSessionCount').valueChanges.subscribe();

    this.payerInit();

    this.eapInfoForm.get('eapState').valueChanges.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((value) => {
      if (value) {
        this.eapStateChanged(value, true);
      }
    });

    this.eapInfoForm.get('eapProvider').valueChanges.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((value) => {
      if (value) {
        this.eapProviderChanged(value);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
  }

  submit(): void {
    this.startSubmitting();

    if (this.eapInfoForm.valid) {
      this.save().pipe(
        finalize(() => this.stopSubmitting())
      ).subscribe({
        next: () => {
          if (!this.useInline) {
            this.snackbar.open('EAP record saved', 'Okay');
          }

          this.closeAdd.emit({ insuranceUpdated: true, bvError: false });
        },
        error: (err) => {
          this.snackbar.open('Error saving EAP', 'Okay');
          // eslint-disable-next-line no-console
          console.error(err);

          if (err?.bvFailed) {
            this.handleBVFailure(err?.bvFailed);
          }
        }
      });
    }
  }

  save(): Observable<ClientInsurance> {
    const body = {
      provider_network_company_id: this.eapInfoForm.value?.eapProvider?.id,
      auth_number: this.eapInfoForm.value.authorizationNumber.trim().toLocaleUpperCase(),
      eap_start_date: this.eapInfoForm.value.startDate.toISOString(),
      eap_end_date: this.eapInfoForm.value.endDate.toISOString(),
      eap_session_count: this.eapInfoForm.value.eapSessionCount
    };

    // if we've already submitted and failed for BV failure,
    // try and update the failed one instead
    // otherwise, create a new insurance object

    let request: Observable<ClientInsurance | never>;
    if (this.bvFailedId) {
      request = this.insuranceHttp.update(this.bvFailedId, body);
    } else {
      request = this.insuranceHttp.create(body);
    }

    // behavior for success is conditional on whether we've failed bv
    // if we've already failed bv, we'll allow the save to continue - only failing once
    // if we haven't already failed bv, we'll add an error to the form and fail out

    return request.pipe(
      tap((data) => {
        this.bvFailedId = data.id;
        takeUntil(this.destroyed$);
      }),
      switchMap((data) => {
        const alreadyFailed = this.bvFailedId != null;
        const verified = data.recent_benefit_verification && data.recent_benefit_verification.aasm_state === 'verified';

        if (verified) {
          // Clear the bvFailedId if verification is successful
          this.bvFailedId = null;
          return of(data);
        }
        // parent form listens for bvFailed to do button stuff
        // bvFailed keeps track of # of attempts
        // If verification hasn't failed before, add an error to the form and fail out
        if (!alreadyFailed) {
          const error = {
            bvFailed: alreadyFailed ? 2 : 1,
            errors: { base: ['Failed Benefit Verification'] }
          };
          return throwError(() => error);
        }
        // If verification has already failed, allow the save to continue without emitting an error
        return of(data);
      })
    );
  }

  /**
   * When EAP State selection is changed, update available provider list.
   */
  eapStateChanged(state: string, eapPref: boolean): void {
    const eapProviderControl = this.eapInfoForm.get('eapProvider');

    if (this.activeStates.includes(state)) {
      eapProviderControl.setValue(null);

      if (eapPref) {
        eapProviderControl.enable();
      }

      this.eapList$ = this.payerDataService.store$.pipe(
        map((ps) => ps.filter((p) => p.eap && p.networkState === state))
      );

      this.eapListOptions$ = this.eapList$.pipe(
        map((ps) => ps.map((p) => ({
          value: p,
          name: p.name
        })))
      );
    } else {
      this.eapInfoForm.get('eapProvider').reset();
      eapProviderControl.disable();
    }
  }

  eapProviderChanged(provider: ProviderNetworkCompany): void {
    if (provider.name.includes(CIGNA)) {
      this.authNumFormatExample
        = 'Your Cigna EAP Authorization number codes can be in the following formats: OP0123456789, 123456789*O, or 1234567891*O. \n\n If your authorization code is formatted like 123456789*O or 1234567891*O, please do not include the letter at the end.';
    } else if (provider.name.includes(OPTUM)) {
      this.authNumFormatExample
        = 'Your Optum/United authorization code should look like A2EG4Q-01. The two digits after the dash must be numeric.';
    }
  }

  /**
   * Load initial insurance payer list.
   */
  payerInit(): void {
    this.payerDataService.refresh().pipe(
      switchMap(() => this.payerDataService.store$),
      map((ps) => ps.filter((p) => !p.isSnap).map((n) => n.networkState)),
      takeUntil(this.destroyed$)
    ).subscribe((pnc) => {
      // create unique list of possible states
      this.activeStates = pnc.filter((p, i) => pnc.indexOf(p) === i);

      // Set insurances according to state
      if (this.activeStates.length) {
        this.eapStateChanged(this.eapInfoForm.value.eapState, this.currentUser.user?.hasEapPref);
      }

      this.usStateOption = USStateOptions.filter((s) => this.activeStates.includes(s.value)).map((s) => ({
        value: s.value,
        name: s.label
      }));
    });
  }

  /**
   * Wrapper for better unit tests.
   */
  startSubmitting(): void {
    this.submitting = true;
    this.bvError = null;
  }

  /**
   * Wrapper for better unit tests.
   */
  stopSubmitting(): void {
    this.submitting = false;
  }

  /**
   * If BV has already failed once, close form and let parent know.
   * If it is first BV failure, display error banner and re-enable form.
   */
  handleBVFailure(bvFailedAttempts: number): void {
    if (bvFailedAttempts > 1) {
      this.closeAdd.emit({ insuranceUpdated: true, bvError: true });
    } else {
      this.bvError = {
        title: 'Unable to verify EAP',
        description: 'We weren’t able to verify your EAP information. Please double check the form below and try again.'
      };
      this.scrollToError();
    }
  }

  saveSelfPay$(): Observable<IPatientInsurance[]> {
    return this.insuranceHttp.addCashPay();
  }

  // Check auth numb is 9, 10, or 12 characters long
  authNumCignaLengthValidator(): ValidatorFn {
    return (input: FormControl): ValidationErrors => (
      this.eapInfoForm?.value.eapProvider?.name.includes(CIGNA) &&
      input.value?.trim().length !== 9 &&
      input.value?.trim().length !== 10 &&
      input.value?.trim().length !== 12
        ? { cignaAuthNumLengthError: true }
        : null);
  }

  // Check auth num only includes numbers after the OP or just numbers
  authNumCignaNumericalValidator(): ValidatorFn {
    return (input: FormControl): ValidationErrors => {
      let isInvalid = false;

      if (this.eapInfoForm?.value.eapProvider?.name.includes(CIGNA)) {
        if (input.value?.slice(0, 2).toLocaleUpperCase() === 'OP') {
          isInvalid = Number.isNaN(+input.value?.trim().slice(2));
        } else {
          isInvalid = Number.isNaN(+input.value?.trim());
        }
      }

      return isInvalid
        ? { cignaAuthNumNumericalError: true }
        : null;
    };
  }

  // Check auth num is 9 characters long (8 characters and a dash)
  authNumOptumLengthValidator(): ValidatorFn {
    return (input: FormControl): ValidationErrors => (
      this.eapInfoForm?.value.eapProvider?.name.includes(OPTUM) && input.value?.trim().length !== 9
        ? { optumAuthNumLengthError: true }
        : null);
  }

  // Check auth num includes a dash
  authNumOptumIncludesDashValidator(): ValidatorFn {
    return (input: FormControl): ValidationErrors => (
      this.eapInfoForm?.value.eapProvider?.name.includes(OPTUM) && !input.value?.includes('-')
        ? { optumAuthNumMissingDashError: true }
        : null);
  }

  // Check last two characters of auth num are digits
  authNumOptumEndCharValidator(): ValidatorFn {
    return (input: FormControl): ValidationErrors => (
      this.eapInfoForm?.value.eapProvider?.name.includes(OPTUM) && Number.isNaN(+input.value?.trim().slice(-2))
        ? { optumAuthNumEndCharError: true }
        : null);
  }

  // Check that the dash is before the last two characters of auth num
  authNumOptumTwoCharAfterDashValidator(): ValidatorFn {
    return (input: FormControl): ValidationErrors => (
      this.eapInfoForm?.value.eapProvider?.name.includes(OPTUM) &&
      input.value?.includes('-') &&
      input.value?.trim().slice(-3)[0] !== '-'
        ? { optumAuthNumDashError: true }
        : null);
  }

  /**
   * Scroll the error banner into view on BV failure.
   */
  scrollToError(): void {
    setTimeout(() => {
      this.bvErrorBanner?.nativeElement?.scrollIntoView();
    }, 0);
  }
}
